import type { ProjectIntent, SiteRagReport } from '../../types/siteRag';
import type { GenerationAssessmentSelection, GridLedStandbyPlanningSnapshot,
  InactiveGenerationImpactAuthority } from '../../types/gridLedStandbyPlanning';
import { generationAssessmentSelectionSchema } from './gridLedStandbyPlanningIntegrity';
import { gridLedStandbyPlanningReportProjectionSchema } from '../../types/gridLedStandbyPlanningProjection';
import { hashGenerationImpactValue } from './integrity';

export function planningSelectionForIntentView(snapshot: GridLedStandbyPlanningSnapshot): GenerationAssessmentSelection {
  if (!snapshot.promotedAt) throw new Error('Planning report context requires an immutable selected revision.');
  return generationAssessmentSelectionSchema.parse({ schemaVersion: 1, kind: 'grid-led-standby-planning',
    assessmentId: snapshot.id, inputHash: snapshot.inputHash, resultHash: snapshot.resultHash, selectedAt: snapshot.promotedAt });
}

/** Only an applicability view. The original declaration and historic applied
 * generation values are never changed or converted into a firm grid rating. */
function withoutInactiveGenerationCapacity(intent: ProjectIntent | undefined | null): ProjectIntent | undefined | null {
  if (!intent) return intent;
  const next = structuredClone(intent);
  next.phases = next.phases?.map(phase => {
    const active = { ...phase, powerSource: 'undecided' as const };
    delete active.powerSourceConfig;
    return active;
  });
  delete next.promotedGenerationScenarioId;
  delete next.promotedGenerationInputHash;
  delete next.promotedGenerationResultHash;
  delete next.promotedGenerationPromotedAt;
  delete next.comparedGenerationScenarioIds;
  delete next.maxPowerStudy;
  delete next.phasePowerPlan;
  if (next.powerSizingMode === 'site-max-power') delete next.powerSizingMode;
  return next;
}

/** Normal Site pointers are server protected. Invalid planning pointers throw
 * instead of falling back to inherited generation capacity. Absence/v1 is an
 * exact reference-preserving no-op. Readiness separately checks the selected
 * immutable revision, current phases, study area and attached source bodies. */
export function deriveIntentForSelectedAssessment<T extends ProjectIntent | undefined | null>(
  intent: T, selection?: GenerationAssessmentSelection,
): T {
  if (selection?.kind !== 'grid-led-standby-planning') return intent;
  generationAssessmentSelectionSchema.parse(selection);
  return withoutInactiveGenerationCapacity(intent) as T;
}

/** Frozen report consumers use the protected strict public projection. No
 * caller-provided Intent marker can switch off an assessment or its holds. */
export function deriveIntentForPlanningReport<T extends ProjectIntent | undefined | null>(
  report: Pick<SiteRagReport, 'gridLedStandbyPlanningAtGen' | 'gridLedStandbyPlanningProjection'>,
  intent: T,
): T {
  if (report.gridLedStandbyPlanningAtGen == null && report.gridLedStandbyPlanningProjection == null) return intent;
  gridLedStandbyPlanningReportProjectionSchema.parse(report.gridLedStandbyPlanningProjection);
  return withoutInactiveGenerationCapacity(intent) as T;
}

/** Private evidence of the applicability decision, frozen alongside the full
 * original projectIntentAtGen. Missing old hashes stay missing, never guessed. */
export function inactiveGenerationImpactAuthority(
  intent: ProjectIntent | undefined | null, snapshot: GridLedStandbyPlanningSnapshot,
): InactiveGenerationImpactAuthority {
  return {
    schemaVersion: 1, selectedAssessmentKind: 'grid-led-standby-planning',
    selectedAssessmentId: snapshot.id, selectedInputHash: snapshot.inputHash,
    selectedResultHash: snapshot.resultHash, selectedAt: planningSelectionForIntentView(snapshot).selectedAt,
    firmCapacityQualification: 'indeterminate', engineeringQualification: 'indeterminate',
    ...(intent?.promotedGenerationScenarioId ? { inactivePriorV1: {
      scenarioId: intent.promotedGenerationScenarioId,
      ...(intent.promotedGenerationInputHash ? { inputHash: intent.promotedGenerationInputHash } : {}),
      ...(intent.promotedGenerationResultHash ? { resultHash: intent.promotedGenerationResultHash } : {}),
      ...(intent.promotedGenerationPromotedAt ? { promotedAt: intent.promotedGenerationPromotedAt } : {}),
    } } : {}),
    inactiveAppliedPhasePowerPlan: intent?.phasePowerPlan != null,
    inactiveMaxPowerStudy: intent?.maxPowerStudy != null,
  };
}

export const GRID_LED_PLANNING_FIRM_QUALIFICATION_NOTICE =
  'The selected grid-led standby assessment supplies static planning arithmetic only. Firm-capacity and engineering qualification remain indeterminate. Declared firm-load and accreditation targets, cooling, water, fuel and interconnection constraints remain requirements to resolve; no earlier generation capacity or reserve calculation satisfies them.';

export function selectedAssessmentSedIdentity(selection?: GenerationAssessmentSelection): string | undefined {
  return selection?.kind !== 'grid-led-standby-planning' ? undefined
    : hashGenerationImpactValue(generationAssessmentSelectionSchema.parse(selection));
}

/** Canonical Intent is deliberately unchanged on selection, so its hash alone
 * cannot qualify a SED compiled with an inactive prior generation overlay. */
export function assertPlanningSedAssessmentIdentity(
  selection: GenerationAssessmentSelection | undefined,
  sed: { builtForGenerationAssessmentHash?: string },
): void {
  if (selection?.kind === 'grid-led-standby-planning'
    && sed.builtForGenerationAssessmentHash !== selectedAssessmentSedIdentity(selection)) {
    throw new Error('Selected planning assessment changed since the SED was built. Rebuild Site Evidence through the normal workflow before report generation.');
  }
}
